import React, { useState, useEffect } from 'react';
import {
  FileJson, AlertTriangle, Key, Globe, Shield, ExternalLink,
  Eye, EyeOff, Copy, Check, ChevronDown, ChevronUp, Search,
  Lock, AlertCircle, Info, Database
} from 'lucide-react';

/**
 * SwaggerSpy Results Component
 * Displays OSINT findings from SwaggerHub scanning
 */
const SwaggerSpyResults = ({ scanId, domain, results }) => {
  const [showSecrets, setShowSecrets] = useState(false);
  const [expandedApis, setExpandedApis] = useState({});
  const [copiedItem, setCopiedItem] = useState(null);
  const [filter, setFilter] = useState('all');

  // Parse results if string
  const data = typeof results === 'string' ? JSON.parse(results) : results;

  const {
    apis_found = [],
    secrets_found = [],
    endpoints = [],
    summary = {},
    raw_output = ''
  } = data || {};

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const getRiskColor = (risk) => {
    switch (risk?.toUpperCase()) {
      case 'CRITICAL': return 'text-red-500 bg-red-500/20';
      case 'HIGH': return 'text-orange-500 bg-orange-500/20';
      case 'MEDIUM': return 'text-yellow-500 bg-yellow-500/20';
      case 'LOW': return 'text-blue-500 bg-blue-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getSecretTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'api key':
      case 'heroku api key':
        return <Key className="w-4 h-4" />;
      case 'token':
      case 'bearer token':
        return <Lock className="w-4 h-4" />;
      case 'password':
        return <Shield className="w-4 h-4" />;
      case 'email':
        return <Globe className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (!data || (apis_found.length === 0 && secrets_found.length === 0)) {
    return (
      <div className="bg-brand-black rounded-xl p-6 border border-gray-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Shield className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">SwaggerSpy OSINT</h3>
            <p className="text-sm text-gray-400">Recherche sur SwaggerHub</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
          <Check className="w-5 h-5 text-green-500" />
          <div>
            <p className="text-green-400 font-medium">No exposure detected</p>
            <p className="text-sm text-gray-400">
              No public API documentation found for {domain} on SwaggerHub
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <FileJson className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">SwaggerSpy OSINT</h3>
              <p className="text-sm text-gray-400">Resultats pour {domain}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(summary.risk_level)}`}>
            {summary.risk_level || 'INFO'}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-brand-black-light rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white">{summary.total_apis || apis_found.length}</div>
            <div className="text-sm text-gray-400">Exposed APIs</div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-orange-500">{summary.total_secrets || secrets_found.length}</div>
            <div className="text-sm text-gray-400">Secrets Detectes</div>
          </div>
          <div className="bg-brand-black-light rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{summary.total_endpoints || endpoints.length}</div>
            <div className="text-sm text-gray-400">Endpoints</div>
          </div>
        </div>
      </div>

      {/* Secrets Section */}
      {secrets_found.length > 0 && (
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h4 className="text-white font-semibold">Exposed Secrets ({secrets_found.length})</h4>
            </div>
            <button
              onClick={() => setShowSecrets(!showSecrets)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
            >
              {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showSecrets ? 'Masquer' : 'Afficher'}
            </button>
          </div>

          <div className="space-y-3">
            {secrets_found.slice(0, showSecrets ? undefined : 5).map((secret, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-brand-black-light rounded-lg border border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg text-orange-500">
                    {getSecretTypeIcon(secret.type)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{secret.type}</div>
                    <div className="text-xs text-gray-400 font-mono">
                      {showSecrets ? secret.value : '••••••••••••••••••••'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(secret.value, `secret-${idx}`)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {copiedItem === `secret-${idx}` ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              </div>
            ))}
            {!showSecrets && secrets_found.length > 5 && (
              <p className="text-sm text-gray-500 text-center">
                +{secrets_found.length - 5} autres secrets...
              </p>
            )}
          </div>
        </div>
      )}

      {/* APIs Found Section */}
      {apis_found.length > 0 && (
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-blue-400" />
            <h4 className="text-white font-semibold">APIs Discovered ({apis_found.length})</h4>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {apis_found.slice(0, 20).map((api, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-brand-black-light rounded-lg border border-gray-700 group"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Globe className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300 font-mono truncate">{api}</span>
                </div>
                <a
                  href={api}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded-lg transition-all"
                >
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
              </div>
            ))}
            {apis_found.length > 20 && (
              <p className="text-sm text-gray-500 text-center py-2">
                +{apis_found.length - 20} autres APIs...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Endpoints Section */}
      {endpoints.length > 0 && (
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-5 h-5 text-purple-400" />
            <h4 className="text-white font-semibold">Endpoints Identifies ({endpoints.length})</h4>
          </div>

          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {endpoints.slice(0, 30).map((endpoint, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs font-mono rounded"
              >
                {endpoint}
              </span>
            ))}
            {endpoints.length > 30 && (
              <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                +{endpoints.length - 30}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {secrets_found.length > 0 && (
        <div className="p-6 bg-red-500/10 border-t border-red-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="text-red-400 font-semibold mb-2">Security Recommendations</h5>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>- Immediately revoke exposed credentials</li>
                <li>- Delete or make private the API specs on SwaggerHub</li>
                <li>- Audit access using these credentials</li>
                <li>- Implement secret detection in CI/CD</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwaggerSpyResults;
